/**
 * Defines dialogue error content that will be shown after specific actions.
 */

import React from 'react';
import { string } from 'prop-types';
import { ESERVICE_MENU_SAVE_CLOSE } from '../components/EServiceFormMenu/eservicemenu.actions';

export const TIMEOUT_TYPE = 'TIMEOUT_TYPE';
export interface dialogueErrorBody {
  body: JSX.Element;
  buttons: dialogueErrorButton[];
  type?: string;
}
export interface dialogueErrorButton {
  text: string;
  cssClassNames?: string;
  redirectUrl?: string;
  action?: string;
}

function getErrorBodyVipSPSessionTimeoutJsx() {
  return (
    <React.Fragment>
      <div className="header">
        <h2>Inaktivitet</h2>
      </div>
      <p>Du har blivit utloggad på grund av inaktivitet.</p>
    </React.Fragment>
  );
}

export function getErrorBodyVipSPSessionTimeout(
  redirectUrl: string,
  translations?: (key: string, defaultValue?: string) => string
): dialogueErrorBody {
  return {
    body: getErrorBodyVipSPSessionTimeoutJsx(),
    type: TIMEOUT_TYPE,

    buttons: [
      {
        text: 'Ok',
        cssClassNames: 'button',
        redirectUrl: redirectUrl,
      },
    ],
  };
}

function getErrorBodyNoOfOrdersTodayReachedJsx() {
  return (
    <React.Fragment>
      <div className="header">
        <h2>Max beställningar per dygn</h2>
      </div>
      <p>Du har uppnått max antal beställningar man får göra per dygn.</p>
    </React.Fragment>
  );
}

export function getErrorBodyNoOfOrdersTodayReached(): dialogueErrorBody {
  return {
    body: getErrorBodyNoOfOrdersTodayReachedJsx(),

    buttons: [
      {
        text: 'Ok',
        cssClassNames: 'button',
      },
    ],
  };
}

function getErrorBodyJwtTokenTimeoutJsx() {
  return (
    <React.Fragment>
      <div className="header">
        <h2>Ogiltig session</h2>
      </div>
      <p>Din session har blivit ogiltig och du loggas ut.</p>
    </React.Fragment>
  );
}

export function getErrorBodyJwtTokenTimeout(redirectUrl: string): dialogueErrorBody {
  return {
    body: getErrorBodyJwtTokenTimeoutJsx(),
    type: TIMEOUT_TYPE,
    buttons: [
      {
        text: 'Ok',
        cssClassNames: 'button',
        redirectUrl: redirectUrl,
      },
    ],
  };
}

export function getErrorBodyApiErrorOnSaveJsx(logId: string) {
  return (
    <React.Fragment>
      <div className="header">
        <h2>Ett oväntat fel har inträffat</h2>
      </div>
      <p>Du kan stänga ner formuläret eller avbryta och försöka spara igen om en stund.</p>
      {logId && (
        <React.Fragment>
          <p>Om felet kvarstår kan du kontakta Socialstyrelsen och ange följande felkod:</p>
          <div className="error-code">{logId}</div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export function getErrorBodyApiErrorOnSave(redirectUrl: string, logId: string): dialogueErrorBody {
  return {
    body: getErrorBodyApiErrorOnSaveJsx(logId),

    buttons: [
      {
        text: 'Stäng',
        cssClassNames: 'button button--white',
        redirectUrl: redirectUrl,
      },
      {
        text: 'Avbryt',
        cssClassNames: 'button',
        redirectUrl: null,
      },
    ],
  };
}

function defaultTranslation(
  translation: (key: string, defaultValue?: string) => string,
  translationPath: string,
  defaultString: string
): string {
  if (translation == null) return defaultString;

  return translation(translationPath);
}

function getErrorBodyApiErrorOnSaveCloseJsx(
  logId: string,
  translations?: (key: string, defaultValue?: string) => string
) {
  return (
    <React.Fragment>
      <div className="header">
        <h2>
          {defaultTranslation(translations, 'dialougeErrors/unexpectedErrorHeader', 'Ett oväntat fel har inträffat')}
        </h2>
      </div>
      <p>
        {defaultTranslation(
          translations,
          'dialougeErrors/cancelAndSaveText',
          'Du kan avbryta och försöka spara och stänga igen om en stund.'
        )}
      </p>
      {logId && (
        <React.Fragment>
          <p>
            {defaultTranslation(
              translations,
              'dialougeErrors/errorCodeText',
              'Om felet kvarstår kan du kontakta Socialstyrelsen och ange följande felkod:'
            )}
          </p>
          <div className="error-code">{logId}</div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export function getErrorBodyApiErrorOnSaveClose(
  redirectUrl: string,
  logId: string,
  translations?: (key: string, defaultValue?: string) => string
): dialogueErrorBody {
  return {
    body: getErrorBodyApiErrorOnSaveCloseJsx(logId, translations),

    buttons: [
      {
        text: defaultTranslation(translations, 'dialougeErrors/closeAnywaysButton', 'Stäng ändå'),
        cssClassNames: 'button button--white',
        redirectUrl: redirectUrl,
      },
      {
        text: defaultTranslation(translations, 'dialougeErrors/cancelbutton', 'Avbryt'),
        cssClassNames: 'button',
        redirectUrl: null,
      },
    ],
  };
}

function getErrorBodyApiErrorOnSendJsx(logId: string) {
  return (
    <React.Fragment>
      <div className="header">
        <h2>Ett oväntat fel har inträffat</h2>
      </div>
      <p>
        Försök skicka in beställningen igen om en stund. Eller välj spara och stäng och skicka in beställningen vid ett
        senare tillfälle.
      </p>
      {logId && (
        <React.Fragment>
          <p>Om felet kvarstår kan du kontakta Socialstyrelsen och ange följande felkod:</p>
          <div className="error-code">{logId}</div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export function getErrorBodyApiErrorOnSend(logId: string): dialogueErrorBody {
  return {
    body: getErrorBodyApiErrorOnSendJsx(logId),

    buttons: [
      {
        text: 'Spara och stäng',
        cssClassNames: 'button button--white',
        action: ESERVICE_MENU_SAVE_CLOSE,
      },
      {
        text: 'Ok',
        cssClassNames: 'button',
        redirectUrl: null,
      },
    ],
  };
}

export function getErrorBodyOpenFormApiError(logId: string): dialogueErrorBody {
  return {
    body: getErrorBodyApiErrorOpenFormJsx(logId),
    buttons: [
      {
        text: 'Stäng',
        cssClassNames: 'button',
        redirectUrl: null,
      },
    ],
  };
}

function getErrorBodyApiErrorOpenFormJsx(logId: string): JSX.Element {
  return (
    <React.Fragment>
      <div className="header">
        <h2>Ett oväntat fel har inträffat</h2>
      </div>
      <p>Du kan stänga ner denna dialogruta och testa igen om en stund.</p>
      {logId && (
        <React.Fragment>
          <p>Om felet kvarstår kan du kontakta Socialstyrelsen och ange följande felkod:</p>
          <div className="error-code">{logId}</div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}
